import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

function diff(target: number) {
  const total = Math.max(0, target - Date.now());
  const days = Math.floor(total / 86_400_000);
  const hours = Math.floor((total % 86_400_000) / 3_600_000);
  const minutes = Math.floor((total % 3_600_000) / 60_000);
  const seconds = Math.floor((total % 60_000) / 1000);
  return { total, days, hours, minutes, seconds };
}

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Αντίστροφη μέτρηση μέχρι τη λήξη μιας προσφοράς.
 * - `compact` = μικρή έκδοση για κάρτες λίστας.
 */
export function OfferCountdown({
  endsAt,
  compact = false,
  className = "",
}: {
  endsAt: string | null | undefined;
  compact?: boolean;
  className?: string;
}) {
  const target = endsAt ? new Date(endsAt).getTime() : NaN;
  const valid = Number.isFinite(target);
  // Render a same-size placeholder (zeroed values) on the server and on the
  // first client render so the HTML matches exactly — computing real seconds
  // during render diverges between server/client and triggers a hydration
  // mismatch (React #418). Crucially the placeholder reserves the SAME height
  // as the live countdown, so filling in the real values after mount produces
  // ZERO layout shift (no CLS) instead of the badge popping into existence.
  const [t, setT] = useState<ReturnType<typeof diff> | null>(null);

  useEffect(() => {
    if (!valid) {
      setT(null);
      return;
    }
    setT(diff(target));
    const id = setInterval(() => setT(diff(target)), 1000);
    return () => clearInterval(id);
  }, [target, valid]);

  if (!valid) return null;
  // After mount we know the real remaining time: hide once the offer expired.
  if (t && t.total <= 0) return null;

  // `t` is null until mounted → show zeroed placeholder of identical size.
  const v = t ?? { total: 1, days: 0, hours: 0, minutes: 0, seconds: 0 };

  if (compact) {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-0.5 rounded-full bg-destructive/90 px-2 py-0.5 text-[10px] font-bold tabular-nums text-destructive-foreground shadow",
          className
        )}
        aria-label="Χρόνος λήξης προσφοράς"
      >
        <span className="text-[9px]">⏱</span>
        <span>
          {v.days > 0 ? `${v.days}η ` : ""}
          {pad(v.hours)}:{pad(v.minutes)}:{pad(v.seconds)}
        </span>
      </div>
    );
  }

  const cells: { v: number; l: string }[] = [
    { v: v.days, l: "Ημέρες" },
    { v: v.hours, l: "Ώρες" },
    { v: v.minutes, l: "Λεπτά" },
    { v: v.seconds, l: "Δευτ." },
  ];

  return (
    <div className={className}>
      <div className="mb-1.5 text-sm font-semibold text-destructive">
        Η προσφορά λήγει σε:
      </div>
      <div className="flex gap-2">
        {cells.map((c) => (
          <div
            key={c.l}
            className="flex min-w-[3.25rem] flex-col items-center rounded-md bg-destructive px-2 py-1.5 text-destructive-foreground"
          >
            <span className="text-xl font-bold tabular-nums leading-none">{pad(c.v)}</span>
            <span className="mt-1 text-[10px] uppercase tracking-wide opacity-90">{c.l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}